import { readAuthSession, type AccountRole, type EncryptedSecretEnvelope, type EncryptedSessionEnvelope, type WrappedDeviceMasterKey } from "@tensilelabs/auth-client";
import type {
  ComponentCandidate,
  ConnectorBubble,
  CoverageReport,
  DecisionCapture,
  PreflightPlan,
  TemplateVersion,
} from "./lib/types";

/** Base URL for every orchestrator/worker call. `VITE_API_BASE_URL` overrides the same origin. */
export const apiBaseUrl = (): string => {
  const configured = import.meta.env?.VITE_API_BASE_URL;
  if (configured) return configured.replace(/\/$/, "");
  return typeof window === "undefined" ? "http://localhost:8088" : window.location.origin;
};

/**
 * Origin for long-lived orchestrator transports that must not be proxied by the edge Worker.
 *
 * REST and SSE calls stay on the app origin (the Worker forwards orchestrator paths). VNC is a
 * WebSocket, and a Worker cannot tunnel a socket without terminating it; Cloudflare also names
 * Worker routes overlapping a tunnel host as a cause of `websocket: bad handshake`. So the
 * browser dials the tunnel hostname directly. Defaults to the app origin for local development.
 */
export const upstreamBaseUrl = (): string => {
  const configured = import.meta.env?.VITE_UPSTREAM_BASE_URL;
  if (configured) return configured.replace(/\/$/, "");
  return apiBaseUrl();
};

/**
 * Explains a 404 on an `/api/v1/` path.
 *
 * The edge Worker owns the whole `/api/v1/` surface: auth, vault, account, catalog, and the
 * orchestrator passthrough. A 404 there therefore never means "this feature does not exist" — it
 * means one of three things, and naming the origin is what makes the difference visible in the
 * browser console instead of a bare status line.
 */
function notFoundHint(path: string): string {
  if (!path.startsWith("/api/v1/")) return "";
  const base = apiBaseUrl();
  return (
    ` — no route for ${path} at ${base}. The edge Worker owns every /api/v1/ path, so this ` +
    "origin is either not the Worker (a Vite dev server or the orchestrator answers /api/v1/ " +
    "with 404) or it is running a deploy that predates the route. Set VITE_API_BASE_URL to the " +
    "Worker origin, or check `GET /api/v1/health` for the `store` and `features` fields."
  );
}

/** The bearer token from the SDK session (`tensile.auth.session`) — the single source of truth. */
export function authToken(): string {
  return readAuthSession()?.token ?? "";
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Defaults to true; auth endpoints set false. */
  auth?: boolean;
}

async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (options.auth !== false) {
    const token = authToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }
  if (!response.ok) {
    const structured = typeof data === "object" && data !== null && "error" in data;
    const message = structured
      ? String((data as { error: unknown }).error)
      : `${path} failed with ${response.status}`;
    // A 404 that carries our own `{ error }` body is a real domain answer ("Secret not found").
    // A 404 with no body, or with HTML, is a backend that has never heard of the route.
    const hint = response.status === 404 && !structured ? notFoundHint(path) : "";
    throw new ApiError(`${message}${hint}`, response.status);
  }
  return data as T;
}

// ---------------------------------------------------------------------------------------
// TensileAuth (Phase 2) — OTP, passkeys, account, devices, invitations, approvals
// ---------------------------------------------------------------------------------------

export interface OtpTicket {
  [key: string]: unknown;
}

export interface AuthSessionResponse {
  token: string;
  user_id: string;
  account_id: string;
  role: AccountRole;
  app_roles: Record<string, AccountRole>;
  device_envelope?: WrappedDeviceMasterKey;
}

export interface AccountMember {
  user_id: string;
  email: string;
  display_name: string;
  role: AccountRole;
  app_roles: Record<string, AccountRole>;
  invited_by: string;
  status: "active" | "invited";
  created_at: string;
}

export interface ApprovalRequest {
  request_id: string;
  account_id: string;
  template_id: string;
  template_name: string;
  created_by_user_id: string;
  created_by_email: string;
  summary: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
}

export interface AccountSnapshot {
  account_id: string;
  account_name: string;
  owner_id: string;
  enabled_apps: string[];
  user: AccountMember | null;
  devices: WrappedDeviceMasterKey[];
  members: AccountMember[];
  approvals: ApprovalRequest[];
}

export function requestOtp(email: string): Promise<{ ticket: OtpTicket; dev_otp_preview?: string }> {
  return apiRequest("/api/v1/auth/otp/request", { method: "POST", body: { email }, auth: false });
}

export function verifyOtp(args: {
  email: string;
  code: string;
  ticket: OtpTicket;
}): Promise<{ verified: boolean; webauthn_rp_id: string; challenge_b64: string }> {
  return apiRequest("/api/v1/auth/otp/verify", {
    method: "POST",
    auth: false,
    body: { email: args.email, otp_code: args.code, ticket: args.ticket },
  });
}

export function registerPasskey(args: {
  email: string;
  displayName?: string;
  deviceEnvelope: WrappedDeviceMasterKey;
}): Promise<AuthSessionResponse> {
  return apiRequest("/api/v1/auth/passkey/register", {
    method: "POST",
    auth: false,
    body: {
      email: args.email,
      display_name: args.displayName,
      device_envelope: args.deviceEnvelope,
    },
  });
}

export function passkeyLogin(args: {
  email: string;
  deviceId?: string;
}): Promise<AuthSessionResponse> {
  return apiRequest("/api/v1/auth/passkey/login", {
    method: "POST",
    auth: false,
    body: { email: args.email, device_id: args.deviceId },
  });
}

export function fetchAccount(): Promise<AccountSnapshot> {
  return apiRequest("/api/v1/account");
}

export function addDevice(envelope: WrappedDeviceMasterKey): Promise<{ device: WrappedDeviceMasterKey }> {
  return apiRequest("/api/v1/account/devices", {
    method: "POST",
    body: { device_envelope: envelope },
  });
}

export function deleteDevice(deviceId: string): Promise<{ deleted: string }> {
  return apiRequest(`/api/v1/account/devices/${encodeURIComponent(deviceId)}`, {
    method: "DELETE",
  });
}

export function inviteMember(args: {
  email: string;
  role: Exclude<AccountRole, "owner">;
  displayName?: string;
}): Promise<{ member: AccountMember }> {
  return apiRequest("/api/v1/account/invitations", {
    method: "POST",
    body: { email: args.email, role: args.role, display_name: args.displayName },
  });
}

export function resolveApproval(
  requestId: string,
  status: "approved" | "rejected",
): Promise<{ approval_request: ApprovalRequest }> {
  return apiRequest(`/api/v1/account/approvals/${encodeURIComponent(requestId)}`, {
    method: "PATCH",
    body: { status },
  });
}

// ---------------------------------------------------------------------------------------
// Settings & Secrets Vault (Page 4)
// ---------------------------------------------------------------------------------------

export function fetchVaultSecrets(): Promise<{ secrets: EncryptedSecretEnvelope[] }> {
  return apiRequest("/api/v1/vault/secrets");
}

export function putVaultSecret(
  envelope: EncryptedSecretEnvelope,
): Promise<{ secret: EncryptedSecretEnvelope }> {
  return apiRequest(`/api/v1/vault/secrets/${encodeURIComponent(envelope.secret_id)}`, {
    method: "PUT",
    body: envelope,
  });
}

export function deleteVaultSecret(secretId: string): Promise<{ deleted: string }> {
  return apiRequest(`/api/v1/vault/secrets/${encodeURIComponent(secretId)}`, {
    method: "DELETE",
  });
}

export function fetchVaultSessions(): Promise<{ sessions: EncryptedSessionEnvelope[] }> {
  return apiRequest("/api/v1/vault/sessions");
}

export function deleteVaultSession(domain: string): Promise<{ deleted: string }> {
  return apiRequest(`/api/v1/vault/sessions/${encodeURIComponent(domain)}`, {
    method: "DELETE",
  });
}

export interface StreamEvent {
  type: "token" | "usage" | "error" | "done" | string;
  content?: string;
  kind?: string;
  message?: string;
  usage?: Record<string, number>;
}

/** `fetch`-streamed POST: JWT in the header, not EventSource. */
export async function streamSsePost<T extends { type: string }>(
  path: string,
  body: Record<string, unknown>,
  onEvent: (event: T) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${authToken()}`,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => "");
    throw new ApiError(detail || `stream failed: ${response.status}`, response.status);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let type = "message";
      const data: string[] = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) type = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trim());
        // Comments (`: keep-alive`) are ignored.
      }
      if (data.length) {
        onEvent({ type, ...(JSON.parse(data.join("\n")) as Record<string, unknown>) } as T);
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

export function streamChat(
  body: Record<string, unknown>,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamSsePost<StreamEvent>("/api/v1/llm/stream", body, onEvent, signal);
}

export async function saveDraft(args: {
  accountId: string;
  templateId: string;
  draftId: string;
  revision: number;
  name: string;
  description: string;
  plan: unknown;
  dashboard: unknown;
  flowchart: unknown;
}): Promise<{ optimistic_revision: number }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/templates/${args.templateId}/drafts/${args.draftId}?account_id=${args.accountId}`,
    {
      method: "PUT",
      headers: { "content-type": "application/json", authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({
        plan: args.plan,
        dashboard_schema: args.dashboard,
        flowchart_view: args.flowchart,
        name: args.name,
        description: args.description,
        status: "ready_for_review",
        incomplete_sections: [],
        optimistic_revision: args.revision,
      }),
    },
  );
  if (response.status === 409) throw new Error("This draft changed on another device. Reload before saving.");
  if (!response.ok) throw new Error(`save failed: ${response.status}`);
  return response.json();
}

export async function finalizeDraft(args: {
  accountId: string;
  templateId: string;
  draftId: string;
}): Promise<{ outcome: "version" | "change_request"; version?: TemplateVersion }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/templates/${args.templateId}/drafts/${args.draftId}/finalize?account_id=${args.accountId}`,
    {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ note: "Finalize from the builder" }),
    },
  );
  if (!response.ok) throw new Error(`finalize failed: ${response.status}`);
  return response.json();
}

export async function loadBubbles(accountId: string): Promise<ConnectorBubble[]> {
  const response = await fetch(`${apiBaseUrl()}/api/v1/catalog/bubbles?account_id=${accountId}`, {
    headers: { authorization: `Bearer ${authToken()}` },
  });
  // A static preview/SPA fallback can answer 200 with HTML; never surface that as a crash.
  if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) {
    return [];
  }
  return response.json();
}

export async function loadComponentCandidates(
  accountId: string,
  domain?: string,
  axSignature?: string,
): Promise<ComponentCandidate[]> {
  const params = new URLSearchParams({ account_id: accountId });
  if (domain) params.set("domain", domain);
  if (axSignature) params.set("ax_signature", axSignature);
  const response = await fetch(`${apiBaseUrl()}/api/v1/components?${params}`, {
    headers: { authorization: `Bearer ${authToken()}` },
  });
  if (!response.ok) return [];
  return response.json();
}

export async function runPreflight(args: {
  accountId: string;
  templateId: string;
  versionId: string;
  inputs: Record<string, unknown>;
}): Promise<PreflightPlan> {
  const response = await fetch(`${apiBaseUrl()}/api/v1/preflight`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${authToken()}` },
    body: JSON.stringify({
      account_id: args.accountId,
      template_id: args.templateId,
      version_id: args.versionId,
      inputs: args.inputs,
      intent: "run",
    }),
  });
  if (!response.ok) throw new Error(`pre-flight failed: ${response.status}`);
  return response.json();
}

export async function loadCaptures(accountId: string, taskRunId: string): Promise<DecisionCapture[]> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/tasks/${taskRunId}/captures?account_id=${accountId}`,
    { headers: { authorization: `Bearer ${authToken()}` } },
  );
  if (!response.ok) return [];
  return response.json();
}

export async function loadCoverage(accountId: string, templateId: string): Promise<CoverageReport | null> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/templates/${templateId}/coverage?account_id=${accountId}`,
    { headers: { authorization: `Bearer ${authToken()}` } },
  );
  if (!response.ok) return null;
  return response.json();
}

export interface SecretSummary {
  name: string;
  category: string;
  is_agent_dynamic: boolean;
  updated_at: string;
}

export interface ConfigSession {
  task_id: string;
  account_id: string;
  connector_id: string;
  session_token: string;
  vnc_host: string;
  vnc_port: number;
  config: boolean;
  restored_cookies_count: number;
  expires_at?: string | null;
}

function authed(init: RequestInit = {}): RequestInit {
  return {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      "content-type": "application/json",
      authorization: `Bearer ${authToken()}`,
    },
  };
}

export async function putSecret(accountId: string, envelope: unknown): Promise<SecretSummary> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/secrets`,
    authed({ method: "POST", body: JSON.stringify({ account_id: accountId, envelope }) }),
  );
  if (!response.ok) throw new Error(`store secret failed: ${response.status}`);
  return response.json();
}

export async function listSecrets(accountId: string): Promise<SecretSummary[]> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/secrets?account_id=${accountId}`,
    authed({ method: "GET" }),
  );
  if (!response.ok) return [];
  return response.json();
}

export async function configureConnector(accountId: string, entryId: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/configure`,
    authed({ method: "POST", body: JSON.stringify({ account_id: accountId }) }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `configure failed: ${response.status}`);
  }
}

export async function launchConnectorSession(args: {
  accountId: string;
  entryId: string;
  recipientPublicKeyB64: string;
  decryptSecretKeyB64?: string;
}): Promise<ConfigSession> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${args.entryId}/session/launch`,
    authed({
      method: "POST",
      body: JSON.stringify({
        account_id: args.accountId,
        recipient_public_key_b64: args.recipientPublicKeyB64,
        authorize_decrypt_secret_key_b64: args.decryptSecretKeyB64,
      }),
    }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `launch failed: ${response.status}`);
  }
  return response.json();
}

export async function handbackConnectorSession(args: {
  accountId: string;
  entryId: string;
  sessionId: string;
  token: string;
}): Promise<{ configured: boolean; cookies: number }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${args.entryId}/session/handback`,
    authed({
      method: "POST",
      body: JSON.stringify({
        account_id: args.accountId,
        session_id: args.sessionId,
        token: args.token,
      }),
    }),
  );
  if (!response.ok) throw new Error(`handback failed: ${response.status}`);
  return response.json();
}

export async function endConnectorSession(args: {
  accountId: string;
  entryId: string;
  sessionId: string;
  token: string;
}): Promise<void> {
  await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${args.entryId}/session`,
    authed({
      method: "DELETE",
      body: JSON.stringify({
        account_id: args.accountId,
        session_id: args.sessionId,
        token: args.token,
      }),
    }),
  );
}

export async function clearConnectorCookies(accountId: string, entryId: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/cookies?account_id=${accountId}`,
    authed({ method: "DELETE" }),
  );
  if (!response.ok) throw new Error(`clear cookies failed: ${response.status}`);
}

export function connectorVncUrl(session: ConfigSession, accountId: string): string {
  const base = new URL(upstreamBaseUrl(), typeof window === "undefined" ? "http://localhost" : window.location.origin);
  const protocol = base.protocol === "https:" ? "wss:" : "ws:";
  const params = new URLSearchParams({
    account_id: accountId,
    session_id: session.task_id,
    token: session.session_token,
  });
  return `${protocol}//${base.host}/api/v1/connectors/${session.connector_id}/session/vnc?${params.toString()}`;
}

export async function registerVaultPublicKey(
  accountId: string,
  appId: string,
  publicKeyB64: string,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/vault/public-key`,
    authed({
      method: "POST",
      body: JSON.stringify({ account_id: accountId, app_id: appId, public_key_b64: publicKeyB64 }),
    }),
  );
  if (!response.ok) throw new Error(`register vault key failed: ${response.status}`);
}

export async function beginOAuth(args: {
  accountId: string;
  entryId: string;
  scopes: string[];
}): Promise<{ authorize_url: string; state: string }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${args.entryId}/oauth/connect`,
    authed({
      method: "POST",
      body: JSON.stringify({ account_id: args.accountId, scopes: args.scopes }),
    }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `oauth connect failed: ${response.status}`);
  }
  return response.json();
}

export async function requestCatalogEntry(
  accountId: string,
  entryId: string,
): Promise<{ created: boolean; request: { stage: string } }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/catalog/${entryId}/request`,
    authed({ method: "POST", body: JSON.stringify({ account_id: accountId }) }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `request failed: ${response.status}`);
  }
  return response.json();
}

export interface WhatsAppPairing {
  pairing_id?: string | null;
  state: string;
  qr?: string | null;
  qr_ascii?: string | null;
  expires_at?: string | null;
  phone?: string | null;
  error?: string | null;
  provider_available?: boolean | null;
  /** Present on the orchestrator's pair/start response when an archive was restored. */
  restored?: boolean;
}

function pairingBody(accountId: string): RequestInit {
  return authed({ method: "POST", body: JSON.stringify({ account_id: accountId }) });
}

/**
 * Ceremony base for a `device_pairing` entry.
 *
 * The catalog declares it (`management_ui.pair_endpoint`) and a Rust test asserts every declared
 * endpoint resolves to a registered route, so the declaration is load-bearing rather than
 * documentation: a second pairing adapter can use a different base without a client change.
 */
export function pairingBase(entryId: string, declared?: string | null): string {
  const base = (declared ?? `/api/v1/connectors/${entryId}/pair`).replace(/\/$/, "");
  return `${apiBaseUrl()}${base}`;
}

export async function startWhatsAppPairing(
  accountId: string,
  entryId: string,
  decryptSecretKeyB64?: string,
  declaredPairEndpoint?: string | null,
): Promise<WhatsAppPairing & { restored?: boolean }> {
  const response = await fetch(
    `${pairingBase(entryId, declaredPairEndpoint)}/start`,
    authed({
      method: "POST",
      body: JSON.stringify({
        account_id: accountId,
        authorize_decrypt_secret_key_b64: decryptSecretKeyB64,
      }),
    }),
  );
  const body = (await response.json().catch(() => ({}))) as WhatsAppPairing & {
    error?: string;
  };
  if (!response.ok) throw new Error(body.error ?? `pairing failed: ${response.status}`);
  return body;
}

export async function whatsappPairingStatus(
  accountId: string,
  entryId: string,
): Promise<WhatsAppPairing | null> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/pair/status?account_id=${accountId}`,
    authed({ method: "GET" }),
  );
  if (!response.ok) return null;
  return response.json();
}

export async function completeWhatsAppPairing(
  accountId: string,
  entryId: string,
  declaredPairEndpoint?: string | null,
): Promise<{ configured: boolean; archive_bytes: number }> {
  const response = await fetch(
    `${pairingBase(entryId, declaredPairEndpoint)}/complete`,
    pairingBody(accountId),
  );
  const body = (await response.json().catch(() => ({}))) as {
    configured?: boolean;
    archive_bytes?: number;
    error?: string;
  };
  if (!response.ok) throw new Error(body.error ?? `complete failed: ${response.status}`);
  return { configured: Boolean(body.configured), archive_bytes: body.archive_bytes ?? 0 };
}

export async function deleteWhatsAppSession(accountId: string, entryId: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/session-archive?account_id=${accountId}`,
    authed({ method: "DELETE" }),
  );
  if (!response.ok) throw new Error(`clear session failed: ${response.status}`);
}

export function streamWhatsAppPairing(args: {
  accountId: string;
  entryId: string;
  pairingId: string;
  onEvent: (event: string, data: Record<string, unknown>) => void;
  signal: AbortSignal;
  declaredPairEndpoint?: string | null;
}): Promise<void> {
  const url =
    `${pairingBase(args.entryId, args.declaredPairEndpoint)}/events?` +
    new URLSearchParams({ account_id: args.accountId, pairing_id: args.pairingId }).toString();
  return (async () => {
    const response = await fetch(url, {
      headers: { authorization: `Bearer ${authToken()}` },
      signal: args.signal,
    });
    if (!response.ok || !response.body) {
      throw new Error(`pairing stream failed: ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf("\n\n");
        let event = "message";
        const dataLines: string[] = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length === 0) continue;
        try {
          args.onEvent(event, JSON.parse(dataLines.join("\n")) as Record<string, unknown>);
        } catch {
          // Ignore malformed frames; the stream stays open.
        }
      }
    }
  })();
}

export interface KeyGrantOffer {
  key_id: string;
  public_key_b64: string;
  expires_at: string;
  aad: string;
}

export interface KeyGrantEnvelope {
  key_id: string;
  client_ephemeral_public_key_b64: string;
  nonce_b64: string;
  ciphertext_b64: string;
}

export async function connectorGrantOffer(
  accountId: string,
  entryId: string,
): Promise<KeyGrantOffer> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/session/grant`,
    pairingBody(accountId),
  );
  if (!response.ok) throw new Error(`grant offer failed: ${response.status}`);
  return response.json();
}

export async function storeConnectorGrant(
  accountId: string,
  entryId: string,
  envelope: KeyGrantEnvelope,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/session/grant/${envelope.key_id}`,
    authed({
      method: "PUT",
      body: JSON.stringify({ account_id: accountId, envelope }),
    }),
  );
  if (!response.ok) throw new Error(`store grant failed: ${response.status}`);
}

export async function connectorGrantStatus(
  accountId: string,
  entryId: string,
): Promise<{ active: boolean; expires_at?: string | null }> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/session/grant?account_id=${accountId}`,
    authed({ method: "GET" }),
  );
  if (!response.ok) return { active: false };
  return response.json();
}

export async function revokeConnectorGrant(accountId: string, entryId: string): Promise<void> {
  await fetch(
    `${apiBaseUrl()}/api/v1/connectors/${entryId}/session/grant?account_id=${accountId}`,
    authed({ method: "DELETE" }),
  );
}

// ---------------------------------------------------------------------------------------
// Builder service (Phase 5): templates, threads, turns, probe, run
// ---------------------------------------------------------------------------------------

export interface DraftPayload {
  draft_id: string;
  template_id: string;
  account_id: string;
  name: string;
  description: string;
  plan: TemplateVersion["plan"];
  dashboard_schema: TemplateVersion["dashboard_schema"];
  flowchart_view: TemplateVersion["flowchart_view"];
  status: string;
  incomplete_sections: string[];
  connectors: string[];
  optimistic_revision: number;
  updated_at: string;
}

export interface TemplateSummary {
  template_id: string;
  account_id: string;
  name: string;
  description: string;
  current_version_id?: string;
  draft_id: string;
  status: string;
}

export interface TemplateDetail {
  template: TemplateSummary;
  draft: DraftPayload;
  current_version?: TemplateVersion;
}

export function createTemplate(args: {
  accountId: string;
  name: string;
  description?: string;
}): Promise<TemplateDetail> {
  return apiRequest("/api/v1/templates", {
    method: "POST",
    body: {
      account_id: args.accountId,
      name: args.name,
      description: args.description ?? "",
    },
  });
}

export function getTemplate(templateId: string, accountId: string): Promise<TemplateDetail> {
  return apiRequest(
    `/api/v1/templates/${encodeURIComponent(templateId)}?account_id=${accountId}`,
  );
}

export interface BuilderThread {
  thread_id: string;
  account_id: string;
  template_id: string;
  draft_id: string;
  active_session_id: string;
  token_budget: number;
  tokens_used: number;
}

export function createBuilderThread(args: {
  accountId: string;
  templateId: string;
  draftId: string;
  tokenBudget?: number;
}): Promise<BuilderThread> {
  return apiRequest("/api/v1/builder/threads", {
    method: "POST",
    body: {
      account_id: args.accountId,
      template_id: args.templateId,
      draft_id: args.draftId,
      token_budget: args.tokenBudget ?? 250_000,
    },
  });
}

export function getBuilderThread(threadId: string, accountId: string): Promise<BuilderThread> {
  return apiRequest(
    `/api/v1/builder/threads/${encodeURIComponent(threadId)}?account_id=${accountId}`,
  );
}

export interface BuilderMessageRecord {
  message_id: string;
  thread_id: string;
  session_id: string;
  account_id: string;
  role: "user" | "assistant" | "tool";
  content: string;
  created_at: string;
}

export function listBuilderMessages(
  threadId: string,
  accountId: string,
): Promise<BuilderMessageRecord[]> {
  return apiRequest(
    `/api/v1/builder/threads/${encodeURIComponent(threadId)}/messages?account_id=${accountId}`,
  );
}

export interface BuilderStreamEvent {
  type:
    | "token"
    | "tool"
    | "proposal"
    | "clarification"
    | "note"
    | "usage"
    | "error"
    | "done"
    | string;
  content?: string;
  kind?: string;
  name?: string;
  accepted?: boolean;
  error?: string;
  question?: string;
  payload?: unknown;
  note?: unknown;
  note_revision?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  cost_usd?: number;
  tokens_used?: number;
  rotated?: boolean;
  message?: string;
}

/** One builder turn. The server validates proposals and streams `token*` then `done`. */
export function streamBuilderTurn(args: {
  threadId: string;
  accountId: string;
  message: string;
  draft: DraftPayload | Record<string, unknown>;
  /** Single-writer lease holder; the turn renews it and a second holder gets 409. */
  holder?: string;
  onEvent: (event: BuilderStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return streamSsePost<BuilderStreamEvent>(
    `/api/v1/builder/threads/${encodeURIComponent(args.threadId)}/turn`,
    {
      account_id: args.accountId,
      message: args.message,
      draft: args.draft,
      holder: args.holder,
    },
    args.onEvent,
    args.signal,
  );
}

export interface LeaseResult {
  status: "acquired" | "held";
  holder?: string;
  expires_at?: string;
}

export function acquireBuilderLease(args: {
  threadId: string;
  accountId: string;
  holder: string;
  ttlSeconds?: number;
  /** Explicit "resume here?" takeover. */
  force?: boolean;
}): Promise<LeaseResult> {
  return apiRequest(`/api/v1/builder/threads/${encodeURIComponent(args.threadId)}/lease`, {
    method: "POST",
    body: {
      account_id: args.accountId,
      holder: args.holder,
      ttl_seconds: args.ttlSeconds,
      force: args.force ?? false,
    },
  });
}

export async function releaseBuilderLease(args: {
  threadId: string;
  accountId: string;
  holder: string;
}): Promise<void> {
  try {
    await apiRequest(
      `/api/v1/builder/threads/${encodeURIComponent(args.threadId)}/lease?account_id=${args.accountId}&holder=${encodeURIComponent(args.holder)}`,
      { method: "DELETE" },
    );
  } catch {
    // Best effort: the lease also expires server-side.
  }
}

export interface ProbeSession {
  probe_id: string;
  thread_id: string;
  approved_domains: string[];
  expires_at: string;
}

/** Explicit user action: opens a read-only scan envelope for the given domains. */
export function createProbeSession(args: {
  threadId: string;
  accountId: string;
  approvedDomains: string[];
}): Promise<ProbeSession> {
  return apiRequest(`/api/v1/builder/threads/${encodeURIComponent(args.threadId)}/probe`, {
    method: "POST",
    body: { account_id: args.accountId, approved_domains: args.approvedDomains },
  });
}

export interface CreatedTask {
  task_id: string;
  template_id: string;
  name: string;
}

export function createTask(args: {
  accountId: string;
  templateId: string;
  name: string;
  runIntent?: "discover" | "run";
}): Promise<CreatedTask> {
  return apiRequest("/api/v1/tasks", {
    method: "POST",
    body: {
      account_id: args.accountId,
      template_id: args.templateId,
      name: args.name,
      inputs: {},
      run_intent: args.runIntent ?? "run",
    },
  });
}

export function launchRun(args: {
  accountId: string;
  taskId: string;
  runIntent?: "discover" | "run";
}): Promise<{ run_id: string }> {
  return apiRequest(`/api/v1/tasks/${encodeURIComponent(args.taskId)}/runs`, {
    method: "POST",
    body: {
      account_id: args.accountId,
      run_intent: args.runIntent ?? "run",
      inputs: {},
    },
  });
}
